import EmbeddableCheckbox from "@components/C138_EmbeddableForm/EmbeddableCheckbox";
import ApiError from "@components/GenericApiError/Error";
import Grid from "@components/Grid/Grid";
import { IsLoggedIn } from "@components/GuestView/IsLoggedIn";
import getCookie from "@utilities/getCookie";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/user";
import {
  getProfileContactPreferences,
  setProfileContactPreferences,
} from "../../services/forms";
import Cta from "../Cta/Cta";
import styles from "./C64_MyProfilePreferences.module.scss";

export interface PreferencesProps {
  canContactThankYouMessage: string;
  canContactPreferencesHeaderText: string;
  canContactPreferencesKeepInformedText: string;
  canPhoneText: string;
  canMailText: string;
  canSmsText: string;
  canBulkEmailText: string;
  signUpLbsrTitle: string;
  signUpToLbsrInformationText: string;
  signUpLbsrLabel: string;
  submitText: string;
}

export const MyPreferences = (props: { content: PreferencesProps }) => {
  const { content } = props;
  const {
    canContactThankYouMessage,
    canContactPreferencesHeaderText,
    canContactPreferencesKeepInformedText,
    canPhoneText,
    canMailText,
    canSmsText,
    canBulkEmailText,
    signUpLbsrTitle,
    signUpToLbsrInformationText,
    signUpLbsrLabel,
    submitText,
  } = content;

  const [preferences, setPreferences] = useState<any>({
    canContactByPhone: false,
    canContactByMail: false,
    canContactBySms: false,
    canContactByEmail: false,
    signUpToLBSThink: false,
  });
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>("");

  const { user } = useContext(UserContext);

  useEffect(() => {
    const getPreferences = async () => {
      if (user) {
        try {
          const contactPreferences = await getProfileContactPreferences(
            getCookie("access_token")
          );
          setPreferences(contactPreferences?.result);
        } catch (e) {
          console.log(e);
          setError(true);
        }
      }
    };

    getPreferences();
  }, [user]);

  const changePref = (name, value) => {
    setPreferences({
      ...preferences,
      [name]: value,
    });
    setStatusMessage("");
    setHasSubmitted(false);
  };

  const confirmPreferences = async () => {
    await setProfileContactPreferences(getCookie("access_token"), preferences);
    setHasSubmitted(true);
    setStatusMessage("");
    setTimeout(() => setStatusMessage(canContactThankYouMessage), 500);
  };

  if (error) {
    return <ApiError />;
  }

  return (
    <div className="wrapper">
      <Grid row>
        <Grid column sm={12}>
          <section
            className={clsx("component", "form", styles["profile-preferences"])}
          >
            <Grid row>
              <Grid column md={7} sm={12}>
                <h2 className={styles["section-title"]}>
                  {canContactPreferencesHeaderText}
                </h2>
                <p>{canContactPreferencesKeepInformedText}</p>
                <section>
                  <div className="form-row">
                    <div className="form-field">
                      <EmbeddableCheckbox
                        checked={preferences?.canContactByPhone}
                        id="CanContactByPhone"
                        name="CanContactByPhone"
                        value="true"
                        label={canPhoneText}
                        onChange={(e) =>
                          changePref("canContactByPhone", e.target.checked)
                        }
                      />
                    </div>

                    <div className="form-field">
                      <EmbeddableCheckbox
                        checked={preferences?.canContactByMail}
                        id="CanContactByMail"
                        name="CanContactByMail"
                        value="true"
                        label={canMailText}
                        onChange={(e) =>
                          changePref("canContactByMail", e.target.checked)
                        }
                      />
                    </div>

                    <div className="form-field">
                      <EmbeddableCheckbox
                        checked={preferences?.canContactBySms}
                        id="CanContactBySms"
                        name="canContactBySms"
                        value="true"
                        label={canSmsText}
                        onChange={(e) =>
                          changePref("canContactBySms", e.target.checked)
                        }
                      />
                    </div>

                    <div className="form-field">
                      <EmbeddableCheckbox
                        checked={preferences?.canContactByEmail}
                        id="CanContactByEmail"
                        name="canContactByEmail"
                        value="true"
                        label={canBulkEmailText}
                        onChange={(e) =>
                          changePref("canContactByEmail", e.target.checked)
                        }
                      />
                    </div>
                  </div>
                </section>
                <section>
                  <h2 className={styles["section-title"]}>{signUpLbsrTitle}</h2>
                  <p>{signUpToLbsrInformationText}</p>
                  <div className="form-row">
                    <div className="form-field">
                      <EmbeddableCheckbox
                        checked={preferences?.signUpToLBSThink}
                        id="IsSubscribedToThinkEmail"
                        name="IsSubscribedToThinkEmail"
                        value="true"
                        label={signUpLbsrLabel}
                        onChange={(e) =>
                          changePref("signUpToLBSThink", e.target.checked)
                        }
                      />
                    </div>
                  </div>
                </section>

                <div className={styles["btn-and-text-wrapper"]}>
                  <div className={styles["button-wrapper"]}>
                    <Cta
                      renderAs="button"
                      onClick={() => {
                        confirmPreferences();
                      }}
                      linkText={submitText}
                      type="primary"
                      url="#"
                    />
                  </div>
                </div>
                {hasSubmitted && (
                  <div
                    className="form-row"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {statusMessage && (
                      <h3 id="ThankYouLabel">{statusMessage}</h3>
                    )}
                  </div>
                )}
              </Grid>
            </Grid>
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default IsLoggedIn(MyPreferences);
